import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

type Admin = { id: number; name: string | null; email: string | null };

// "Who can I ask?" - the company's admins, for prompts that tell a member
// only an admin can do something (upgrading, add-ons). Emails are mailto
// links.
const CompanyAdminsList: React.FC<{ entityId: number }> = ({ entityId }) => {
    const [result, setRequest] = useApi<{ success: boolean; admins: Admin[] }>();

    React.useEffect(() => {
        setRequest(buildApiRequestConfig(routes.userSettings.fetchAdmins, [{ param: 'entityId', value: entityId }]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    if (result.isLoading) return <CircularProgress size={16} sx={{ color: 'var(--color-primary)', mt: 1 }} />;
    const admins = result.data?.admins ?? [];
    if (!admins.length) return null;

    return (
        <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>
                {admins.length === 1 ? 'Your company admin' : 'Your company admins'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {admins.map(admin => (
                    <Box key={admin.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'baseline', fontSize: 13 }}>
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{admin.name || admin.email}</Typography>
                        {admin.email && admin.name && (
                            <Typography component="a" href={`mailto:${admin.email}`} sx={{ fontSize: 12.5, color: 'var(--color-primary)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                {admin.email}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default CompanyAdminsList;
